import { useMemo, useRef } from "react"

import { api } from "@/lib/api"

// Stays under MAX_CONTACT_COUNT_COMPANIES on the server (200) per request.
export const CONTACT_COUNT_BLOCK_SIZE = 50

/**
 * The rows of every fixed block the window touches. Scrolling inside a block
 * keeps the same ids, so the cached count request is reused.
 */
export function contactCountBlocks(
	indices: readonly number[],
	idAt: (index: number) => string | undefined,
	blockSize = CONTACT_COUNT_BLOCK_SIZE,
): string[][] {
	const blocks = [...new Set(indices.map((i) => Math.floor(i / blockSize)))]
	return blocks
		.sort((a, b) => a - b)
		.map((block) => {
			const ids: string[] = []
			for (let i = block * blockSize; i < (block + 1) * blockSize; i++) {
				const id = idAt(i)
				if (id) ids.push(id)
			}
			return ids
		})
		.filter((ids) => ids.length > 0)
}

/** People per company for the rows on screen, primary and linked. */
export function useCompanyContactCounts(
	indices: readonly number[],
	idAt: (index: number) => string | undefined,
): Record<string, number> {
	const blocks = contactCountBlocks(indices, idAt)
	const blocksKey = JSON.stringify(blocks)
	const results = api.useQueries((t) =>
		blocks.map((companyIds) =>
			t.company.contactCounts({ companyIds }, { staleTime: 30_000 }),
		),
	)
	const dataKey = results.map((result) => result.dataUpdatedAt).join()
	// A block's ids change as its rows load, which starts a fresh request; keep
	// the last known counts so the column doesn't blank while it runs.
	const known = useRef<Record<string, number>>({})

	return useMemo(
		() => {
			for (const result of results) Object.assign(known.current, result.data)
			return { ...known.current }
		},
		// results is a new array every render; the blocks and fetch times say when it changed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[blocksKey, dataKey],
	)
}
